/**
 * 🏠 **Drawing the storehouse — one 3D model over a footprint of blocks.**
 *
 * **D109, T135.** 🎯 **haugur, 2026-09-03:** *"the photograph, hyper realistic,
 * since its not a structure i build but an object, like a chest but bigger and
 * like a hous… it does not need to be blocky."*
 *
 * ## 🔴 Why this is not in the world mesh
 *
 * The same reason as the driftwood: `mesher.ts` speaks only in axis-aligned
 * boxes, and this is a turf-roofed house with a pitched roof and a plank gable,
 * made from picture 2 by an image-to-3D model
 * (`tools/design-storehouse-model.mjs`). So the mesher skips the building's
 * cells — `bur` and `burhluti` are on `drawnOutsideTheMesh` — and this draws
 * the model over them. ⚠️ **The cells are still there.** Walls stop you, the
 * roof shelters you, and the door is a block you can click, because those are
 * facts about the WORLD and this file only draws.
 *
 * ## 🔴 THE ONE MATERIAL RULE
 *
 * A GLB arrives with `MeshStandardMaterial`. **It is never drawn with that.**
 * Every mesh is re-dressed in `MeshLambertNodeMaterial` with a `texture()`
 * colour node — the exact path `flame.ts` and `smoke.ts` already run under
 * WebGPU in the live game. A texture on a material path nobody had run there is
 * what turned dev and staging black on 2026-09-01, **and WebGPU cannot be
 * checked on this workstation.** ✅ haugur placed the first one on 2026-09-03
 * and the screen stayed alive.
 *
 * ## 🎯 Three kinds of triangle, from his first look
 *
 * *"door is both on front and back, wich is wrong, the door could be in a
 * little different colour that the gable."* The model was made from ONE
 * photograph, so it guessed its back as a mirror of its front. So every
 * triangle is classified (`shared/src/burgeom.ts` — pure, tested, and the
 * design tool draws the same split):
 *
 * | | dressed as |
 * |---|---|
 * | **body** | the model's own texture |
 * | **door** | the model's own texture, **darkened to half** |
 * | **back gable** | 🪵 **the game's own driftwood-plank tile**, one metre per tile, planar — plain planks, no door |
 *
 * The plank tile is the world's `rekaplanki` side face rendered by the same
 * code the world atlas uses, in a one-layer `DataArrayTexture` of its own —
 * the flame's exact texture type — so a graphics-settings rebuild of the world
 * atlas cannot pull a texture out from under this material (the black-world
 * trap of 2026-08).
 *
 * ## The model is loaded ONCE, and cloned per building
 *
 * `BurModel` fetches the GLB, splits and re-dresses it, stands it on the floor
 * centred on its footprint, and keeps that as a template. A chunk with a door
 * in it gets a `clone()` — geometry and material are shared, only the transform
 * is new. ⚠️ A chunk built before the model has loaded gets an empty group that
 * fills itself in when the model arrives.
 *
 * ## Which way round, and how big
 *
 * In the model's own frame the gable with the door is on the **−z** face
 * (`Design/samples/70_storehouse_model.png`, the frame labelled *back*, taken
 * from −z). Our `into` — the way the building extends from the door — is the
 * model's **+z**, and the rotation is `atan2(into.x, into.z)`. It is scaled to
 * **5 m across**, the height keeps that scale (4.9 m), and the length is
 * scaled separately to the footprint's 9 m — 🎯 *"2 to 3 blocks shorter."*
 */

import {
  Box3,
  BufferGeometry,
  DataArrayTexture,
  DoubleSide,
  PlaneGeometry,
  Float32BufferAttribute,
  Group,
  LinearMipmapLinearFilter,
  Mesh,
  NearestFilter,
  type Object3D,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  type Texture,
} from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { int, texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  BUR_BLOCK,
  BUR_FOOTPRINT,
  houseKindOf,
  burCentre,
  HOUSE_SIGN_PLACEMENT,
  HOUSE_SIGN_HEIGHT,
  HOUSE_SIGN_WIDTH,
  houseSignKeys,
  houseSignUri,
  burFacing,
  burFrame,
  CHUNK_SIZE,
  classifyBurFace,
  DOOR_HALF_WIDTH,
  DOOR_TOP,
  frontmostZInPatch,
  isBur,
  renderTile,
  sampleTriangle,
  tilePlans,
  type BurFaceKind,
  type BurFacing,
} from '@lofi/shared';

/** A door, in chunk-local cells. */
export interface BurAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: BurFacing;
  /**
   * 🪧 **Which of the three houses this is** — the door block's key, and the
   * only thing that distinguishes them (D110). Today always `bur`.
   */
  readonly kind: string;
}

export type BurGroup = Group;

/**
 * ☀️ **How much day there is, 0…1 — the sun's intensity as a fraction of full.**
 *
 * 🎯 haugur, on the first fixed one: *"now whole front is much darker than
 * backside, to dark"* — and turning it helped, so it was the sun. The model's
 * Lambert takes the sun harder than the block faces do, which carry their own
 * baked value. So every part gets an **emissive lift of a third of its own
 * colour, scaled by this** — readable in shadow by day, and dark at night,
 * because a storehouse that glowed at midnight would be a lantern.
 * `main.ts` writes it wherever it sets the sun.
 */
export const burDaylight = uniform(1);

/** 🚪 The door's own texture — served with the model, a plain git blob. */
const DOOR_TEXTURE_URL = '/models/bur-door.png';

/**
 * 🪧 **The carved board that says which house this is** — D110.
 *
 * 🎯 **haugur, 2026-09-04:** *"put a sign above the door that reprisents
 * storage."* 🔴 **The three buildings are the SAME MODEL**, so this is not
 * decoration: it is the only thing that tells a búr from a skemma from a
 * smiðja. ⚠️ **If it stops drawing, the game has three identical houses.**
 *
 * **One texture per door type, decoded once and shared by every copy.**
 */
const signTextures = new Map<string, Texture>();
const signMaterials = new Map<string, MeshLambertNodeMaterial>();

/**
 * Decode the boards. Called once; nothing waits on it, and a board that will
 * not decode leaves a bare gable rather than a dead frame.
 */
async function loadHouseSigns(): Promise<void> {
  const loader = new TextureLoader();
  const keys = houseSignKeys();
  await Promise.all(
    keys.map(async (key) => {
      const uri = houseSignUri(key);
      if (uri === undefined) return;
      try {
        const map = await loader.loadAsync(uri);
        map.colorSpace = SRGBColorSpace;
        map.generateMipmaps = false;
        signTextures.set(key, map);
      } catch (err: unknown) {
        console.error(`[bur] the sign for "${key}" did not decode`, err);
      }
    }),
  );
  console.info(`[bur] ${String(signTextures.size)} of ${String(keys.length)} sign(s) ready`);
}

/**
 * The board's material, built once per door type.
 *
 * 🔴 **The same `MeshLambertNodeMaterial` + `texture()` colour node + daylight
 * lift the model's own three faces use.** A sign on a different material would
 * catch the light differently from the wall it is nailed to, and — far worse —
 * it would be a shader permutation nothing in the running game compiles.
 */
function signMaterial(key: string, torch = 0): MeshLambertNodeMaterial | null {
  /**
   * 🔦 **Keyed by the board AND the light it stands in** — 🎯 haugur, minutes
   * after the houses lit up: *"the signs are not lighting up with the torches."*
   *
   * 🔴 **The board is nailed to a wall that brightens and it did not brighten
   * with it**, which reads worse than neither of them lighting: the eye goes
   * straight to the one dark rectangle on a lit gable.
   *
   * ⚠️ **This cache was keyed by the door kind alone**, so the first house to
   * ask fixed the board's brightness for every house in the world. **The same
   * one-value-for-everything fault the houses themselves had**, one object
   * smaller — and it was invisible until the wall behind it could change.
   */
  const step = Math.round(Math.min(1, Math.max(0, torch)) * TORCH_STEPS);
  const id = `${key}:${String(step)}`;
  const had = signMaterials.get(id);
  if (had) return had;
  const map = signTextures.get(key);
  if (map === undefined) return null;
  const material = new MeshLambertNodeMaterial();
  const tile = texture(map, uv());
  const colour = vec3(tile.r, tile.g, tile.b);
  material.colorNode = colour;
  /* 🔦 The SAME lift the wall it is nailed to gets — see `materials()`. A board
     lit differently from its own gable is worse than one not lit at all. */
  setEmissive(
    material,
    colour.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))),
  );
  /* ⚠️ The board's rounded corners are alpha. Without this they draw as black. */
  (material as unknown as { opacityNode: unknown }).opacityNode = tile.a;
  material.transparent = true;
  material.alphaTest = 0.4;
  material.side = DoubleSide;
  signMaterials.set(id, material);
  return material;
}

/**
 * 🪧 One board, on the gable BESIDE the door, in the MODEL's own frame.
 *
 * 🎯 **haugur, 2026-09-04**, looking at the signposts in play: *"its a bit
 * cluttered, i like the size of the sign itself but te post no so much, can we
 * put the signs on the wall on the left side of each door."*
 *
 * ## 🔴 The five positions this board has had, and the one thing they share
 *
 * 🎯 *"the sign is up in the sky"* (scaled twice) · off the front (the model's
 * box, which is the ROOF, because the turf eave overhangs the gable) · inside
 * the house (matrices not updated) · **out in front of it by 41 cm** (the
 * DOOR's bounding box, whose most extreme vertex is a lip nothing else shares)
 * · and now the wall.
 *
 * 🔴 **Every one measured something that was not the wall under the board.**
 * ✅ **`wallZ` is the gable inside the board's own rectangle** — `signWallZ`,
 * which cannot be wrong by an amount unrelated to what the board is nailed to.
 * The rest are numbers we chose, in metres, in `housesigns.ts`.
 */
function signMesh(key: string, wallZ: number, torch = 0): Object3D | null {
  const material = signMaterial(key, torch);
  if (material === null) return null;
  const P = HOUSE_SIGN_PLACEMENT;
  const w = P.width;
  const h = (w * HOUSE_SIGN_HEIGHT) / HOUSE_SIGN_WIDTH;

  const board = new Mesh(new PlaneGeometry(w, h), material);
  /* 🔴 `toTheSide` is POSITIVE and the group is turned to face out, so +x is
     the LEFT of the door as a player walking up to it sees it — which is what
     he asked for, and it was checked in a screenshot rather than reasoned. */
  board.position.set(P.toTheSide, P.centreY, wallZ - P.proud);
  /* Facing out of the gable, which is −z. */
  board.rotation.y = Math.PI;
  return board;
}

/** Every storehouse door in one chunk's blocks. */
export function storehousesIn(blocks: Uint16Array): BurAt[] {
  const out: BurAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array, as driftmesh.ts does. */
    const id = blocks[i] ?? 0;
    if (!isBur(id)) continue;
    const facing = burFacing(id) ?? 'north';
    const x = i % CHUNK_SIZE;
    const y = Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE));
    const z = Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE;
    /* 🪧 D110: the door block's own key. Three buildings share one model and
       the carved board is the only thing that tells them apart. */
    /* 🪧 D110: which of the three, so the right board goes over the door. */
    out.push({ x, y, z, facing, kind: houseKindOf(id) ?? BUR_BLOCK });
  }
  return out;
}

/** The tile the world draws rekaplanki's side with, as its own one-layer array texture. */
function plankTexture(): DataArrayTexture | null {
  const plan = tilePlans().find((p) => p.key === 'rekaplanki' && p.face === 'side');
  if (!plan) return null;
  const size = 64;
  /* Copied into a fresh array: the typed-array generic the renderer wants. */
  const tex = new DataArrayTexture(new Uint8Array(renderTile(plan, size)), size, size, 1);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.magFilter = NearestFilter;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/**
 * ☀️ `emissiveNode` is read by three's `NodeMaterial` at runtime
 * (`NodeMaterial.js`, `setupLighting`) and is simply absent from its type
 * declarations in 0.180 — so it is set through this one typed seam rather
 * than four casts.
 */
function setEmissive(material: MeshLambertNodeMaterial, node: unknown): void {
  (material as unknown as { emissiveNode: unknown }).emissiveNode = node;
}

/**
 * 🔦 **How much a torch beside a house brightens it, at full light.**
 *
 * ⚠️ **Added to the daylight lift, never multiplied.** `mesher.ts` carries the
 * same warning for the same value: **0 means "nothing was added here", not "this
 * is dark"** — multiplying would black out every house in Iceland at noon.
 *
 * 🎯 **Judged by eye against the daylight lift of 0.35** and meant to be argued
 * with: a torch should make a wall readable at night, not make it glow.
 */
const TORCH_LIFT = 0.5;

/**
 * 🔦 **How many steps of torch light a house can take.**
 *
 * 🔴 **This exists so the number of MATERIALS stays bounded.** A material per
 * house would be a shader permutation per house; **five variants is five, for
 * any number of buildings.** ⚠️ **And the graph is identical in every one** —
 * only the uniform's value differs — so they should share one compiled program.
 */
const TORCH_STEPS = 4;

/** The three materials, built once per model from its own colour map. */
function materials(
  map: Texture | null,
  doorMap: Texture | null,
  /** 🔦 Added light at this building, 0–1. */
  torch = 0,
): Record<BurFaceKind, MeshLambertNodeMaterial> {
  const body = new MeshLambertNodeMaterial();
  const door = new MeshLambertNodeMaterial();
  const back = new MeshLambertNodeMaterial();
  /**
   * ☀️ The lift: a third of the face's own colour by daylight, 🔦 **plus
   * whatever the torches around it add.**
   *
   * 🎯 **haugur, 2026-09-04:** *"i placed some kyndlar on the 3 houses, it
   * worked fine but the houses don light up in the dark from the kyndlar."*
   * 🔴 **The house is a MODEL drawn beside the terrain**, so it never saw the
   * world mesh's per-vertex light attribute and knew only about the sun.
   */
  /* 🔴 **`.add()` on a uniform is a construct the running game ALREADY
     compiles** — `flame.ts` does `flameFrame.add(phase)` on a uniform, and
     `rain.ts` names the proven set in as many words: *".round(), .mul(),
     .add(), .sub(), .div() only"*. ⚠️ **A shader cannot be checked on this
     workstation**, so staying inside that set is the whole of the safety
     argument, and it is the rule that a texture on a new material path broke
     when it blacked out dev and staging on 2026-09-01. */
  const lift = burDaylight.mul(0.35).add(uniform(torch * TORCH_LIFT));
  if (map) {
    map.colorSpace = SRGBColorSpace;
    const tile = texture(map, uv());
    const colour = vec3(tile.r, tile.g, tile.b);
    body.colorNode = colour;
    setEmissive(body, colour.mul(lift));
  }
  if (doorMap) {
    /* 🚪 A real door — its own photograph, planar over the doorway. */
    doorMap.colorSpace = SRGBColorSpace;
    const tile = texture(doorMap, uv());
    const colour = vec3(tile.r, tile.g, tile.b);
    door.colorNode = colour;
    setEmissive(door, colour.mul(lift));
  } else if (map) {
    /* Until the door texture arrives: the model's own, darkened. */
    const tile = texture(map, uv());
    const colour = vec3(tile.r, tile.g, tile.b).mul(0.5);
    door.colorNode = colour;
    setEmissive(door, colour.mul(lift));
  }
  const plank = plankTexture();
  if (plank) {
    const tile = texture(plank, uv()).depth(int(0));
    const colour = vec3(tile.r, tile.g, tile.b);
    back.colorNode = colour;
    setEmissive(back, colour.mul(lift));
  }
  return { body, door, backGable: back };
}

/**
 * 🎨 Split every mesh by what its triangles are, and re-dress each part.
 *
 * Back-gable triangles get planar UVs in metres — x and y of the model, at the
 * width's scale — so the plank tile repeats once a metre like the world's own
 * blocks. Everything else keeps the model's UVs.
 */
function redress(
  root: Group,
  box: Box3,
  size: Vector3,
  scale: number,
  doorMap: Texture | null,
): Texture | null {
  const meshes: Mesh[] = [];
  root.traverse((o) => {
    if (o instanceof Mesh) meshes.push(o as Mesh);
  });
  if (meshes.length === 0) return null;
  const first = meshes[0]?.material as { map?: Texture | null } | undefined;
  const modelMap = first?.map ?? null;
  const mats = materials(modelMap, doorMap);

  for (const m of meshes) {
    const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry;
    const P = g.getAttribute('position').array;
    const U = g.getAttribute('uv')?.array ?? null;
    const parts: Record<BurFaceKind, { p: number[]; u: number[] }> = {
      body: { p: [], u: [] },
      door: { p: [], u: [] },
      backGable: { p: [], u: [] },
    };
    for (let i = 0; i + 8 < P.length; i += 9) {
      const kind = classifyBurFace(sampleTriangle(P, i, box.min, size));
      /* eslint-disable-next-line security/detect-object-injection -- `kind` is
         one of three literals from classifyBurFace, never a string from outside. */
      const part = parts[kind];
      if (!part) continue;
      for (let k = 0; k < 9; k++) part.p.push(P[i + k] ?? 0);
      if (kind === 'backGable') {
        for (let v = 0; v < 3; v++) {
          part.u.push(
            ((P[i + v * 3] ?? 0) - box.min.x) * scale,
            ((P[i + v * 3 + 1] ?? 0) - box.min.y) * scale,
          );
        }
      } else if (kind === 'door') {
        /* 🚪 The doorway's own rectangle, 0…1 across and up, from the same
           numbers the classifier drew it with — so the picture fills exactly
           the triangles that were called the door. */
        for (let v = 0; v < 3; v++) {
          const xn = ((P[i + v * 3] ?? 0) - box.min.x) / Math.max(size.x, 1e-6);
          const yn = ((P[i + v * 3 + 1] ?? 0) - box.min.y) / Math.max(size.y, 1e-6);
          part.u.push(
            Math.min(1, Math.max(0, (xn - (0.5 - DOOR_HALF_WIDTH)) / (2 * DOOR_HALF_WIDTH))),
            Math.min(1, Math.max(0, yn / DOOR_TOP)),
          );
        }
      } else if (U) {
        const j = (i / 9) * 6;
        for (let k = 0; k < 6; k++) part.u.push(U[j + k] ?? 0);
      } else {
        part.u.push(0, 0, 0, 0, 0, 0);
      }
    }
    const parent = m.parent;
    if (!parent) continue;
    parent.remove(m);
    for (const kind of ['body', 'door', 'backGable'] as const) {
      /* eslint-disable-next-line security/detect-object-injection -- the same
         three literals, iterated from a const tuple. */
      const d = parts[kind];
      if (!d || d.p.length === 0) continue;
      const bg = new BufferGeometry();
      bg.setAttribute('position', new Float32BufferAttribute(d.p, 3));
      bg.setAttribute('uv', new Float32BufferAttribute(d.u, 2));
      bg.computeVertexNormals();
      // eslint-disable-next-line security/detect-object-injection -- the same literal.
      const nm = new Mesh(bg, mats[kind]);
      /* 🪧 Named so a part can be found again by what it IS. ⚠️ The sign no
         longer asks the door where the wall is — a bounding box answers with
         its most extreme vertex, and the door's is a lip 41 cm proud of the
         gable. See `signWallZ`. */
      nm.name = `bur-${kind}`;
      nm.position.copy(m.position);
      nm.rotation.copy(m.rotation);
      nm.scale.copy(m.scale);
      parent.add(nm);
    }
    (m.material as { dispose?: () => void }).dispose?.();
  }
  /* 🔦 Handed back so `BurModel` can build a LIT variant of the same graph
     later, without re-reading the GLB. */
  return modelMap;
}

/**
 * 🪧 **The wall the carved board hangs on** — the frontmost z, in metres, of the
 * gable inside the board's own rectangle.
 *
 * 🎯 **haugur, 2026-09-07:** the boards *"stand out from the wall."* 🔴 **They
 * stood 41 cm proud of it.** The board hung 1 cm in front of the DOOR's
 * bounding box, and a bounding box keeps the single most extreme vertex it was
 * given: three quarters of the door's vertices are at −4.04 m and a 5% lip
 * reaches −4.44, so the board went to −4.45 while the wall beside the door sits
 * at −4.04. **See `frontmostZInPatch` for the measurement.**
 *
 * ⚠️ **This is the FIFTH position this board has had, and the first four were
 * all the same mistake**: the sky (scaled twice), the roof (the model's box),
 * inside the house (matrices not updated), and out in front of it (the door's
 * box). 🔴 **Every one of them measured something that was not the wall under
 * the board.**
 */
function signWallZ(house: Group): number {
  /**
   * 🔴 **Update the matrices FIRST, or the points are read in the wrong frame.**
   *
   * 🎯 **haugur's photograph, 2026-09-04:** after one fix the boards vanished
   * entirely. A vertex is in its mesh's own units until `matrixWorld` is
   * applied, and `house` has just been assembled and never rendered — so the
   * pivot's scale had not been applied, the gable measured about −2 instead of
   * −4.5, and **the sign was hung inside the building.**
   *
   * 👉 **The lesson is not "be careful" — it is that a number in metres and a
   * number in model units look identical**, and only a photograph told them
   * apart. **Say which frame a number is in, every time.**
   */
  house.updateMatrixWorld(true);
  const P = HOUSE_SIGN_PLACEMENT;
  const boardHeight = (P.width * HOUSE_SIGN_HEIGHT) / HOUSE_SIGN_WIDTH;
  const points: number[] = [];
  const v = new Vector3();
  house.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh !== true) return;
    const attr = mesh.geometry.getAttribute('position');
    if (!attr) return;
    for (let i = 0; i < attr.count; i++) {
      v.fromBufferAttribute(attr, i).applyMatrix4(mesh.matrixWorld);
      points.push(v.x, v.y, v.z);
    }
  });
  const found = frontmostZInPatch(
    points,
    P.toTheSide,
    P.centreY,
    P.width / 2,
    boardHeight / 2,
    /* 🔴 The front half only. The far gable is 9 m behind this one and the
       mesh's winding is inconsistent, so position — not a normal — is what
       tells the two ends apart. */
    0,
  );
  if (found === undefined) {
    /* ⚠️ **This fallback is the OLD fault on purpose**: the model's front edge
       is its roof, so the board stands visibly proud and somebody says so. A
       board that quietly moved half a metre is what cost four rounds of this. */
    console.warn('[bur] no gable found under the sign — it falls back to the model edge');
    return -BUR_FOOTPRINT.length / 2;
  }
  /* 🔴 Loud, because a board in the wrong place is invisible from the code and
     a silent wrong number is what cost four rounds of this. */
  console.info(`[bur] the wall under the carved board is at z = ${found.toFixed(3)} m`);
  return found;
}

export class BurModel {
  private template: Group | null = null;
  /** Where the gable's wall is, once the model has been measured. */
  private signWallZ = -BUR_FOOTPRINT.length / 2;
  private readonly waiting: (() => void)[] = [];
  /** The model's own textures, kept so a lit variant can be built later. */
  private maps: { map: Texture | null; doorMap: Texture | null } = { map: null, doorMap: null };
  /**
   * 🔦 **One material set per step of torch light, built on demand.**
   *
   * 🔴 **Bounded on purpose** — see {@link TORCH_STEPS}. A set per building
   * would be a shader permutation per building; this is five for the whole
   * world, and every one has the identical node graph.
   */
  private readonly litMats = new Map<number, Record<BurFaceKind, MeshLambertNodeMaterial>>();

  /** The materials for a house standing in `light` (0–1) of added torch light. */
  private materialsFor(light: number): Record<BurFaceKind, MeshLambertNodeMaterial> | null {
    const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
    const had = this.litMats.get(step);
    if (had) return had;
    if (this.maps.map === null && this.maps.doorMap === null) return null;
    const made = materials(this.maps.map, this.maps.doorMap, step / TORCH_STEPS);
    this.litMats.set(step, made);
    return made;
  }

  constructor(url: string) {
    /* 🚪 The door's picture and the model, side by side; a door that fails to
       load falls back to the model's own darkened door rather than to nothing. */
    const doorMap = new TextureLoader().loadAsync(DOOR_TEXTURE_URL).catch((err: unknown) => {
      console.error('Storehouse: the door texture did not load', err);
      return null;
    });
    /**
     * 🪧 **The boards wait in the SAME queue as the model, and that is the fix.**
     *
     * 🔴 They used to be loaded separately, on their own promise. A chunk that
     * arrived before they decoded was placed with **no board at all**, and
     * nothing ever placed it again — so haugur's storehouse stood there with a
     * bare gable while the pictures sat decoded in memory.
     *
     * ⚠️ **This class already had the answer**: `waiting` exists precisely
     * because the model itself arrives late. **A second thing that arrives late
     * belongs in the same gate, not in a second one beside it** — the fault this
     * repo has now named four times, and the third time TODAY.
     */
    Promise.all([new GLTFLoader().loadAsync(url), doorMap, loadHouseSigns()])
      .then(([gltf, doorTex]) => {
        const scene = gltf.scene;
        /* 📏 Measure the raw model, split and re-dress it, then stand it on the
           floor centred on its footprint. Width 5 m sets the scale for x and y;
           the length is scaled on its own to the footprint's 9 m. */
        const box = new Box3().setFromObject(scene);
        const size = box.getSize(new Vector3());
        const centre = box.getCenter(new Vector3());
        const s = BUR_FOOTPRINT.width / Math.max(size.x, 1e-6);
        const sz = BUR_FOOTPRINT.length / Math.max(size.z, 1e-6);
        this.maps = { map: redress(scene, box, size, s, doorTex), doorMap: doorTex };
        const pivot = new Group();
        scene.position.set(-centre.x, -box.min.y, -centre.z);
        pivot.add(scene);
        pivot.scale.set(s, s, sz);
        /**
         * 🔴 **A wrapper whose own units are METRES, and it is not decoration.**
         *
         * 🎯 **haugur, 2026-09-04:** *"the sign is up in the sky far from
         * house."* The board was added to the pivot — **which carries the
         * model's scale** — so a 1.2 m board came out five times too big and its
         * z position was multiplied by the length scale into the next field.
         *
         * ⚠️ **The pivot's numbers are in the GLB's own arbitrary units**, which
         * is fine for the model and wrong for anything measured in the world.
         * Everything hung on the building from here — the board, and whatever
         * comes after it — goes on this wrapper instead.
         */
        const house = new Group();
        house.add(pivot);
        /**
         * 🪧 **Where the gable's WALL is, measured — not the model's edge.**
         *
         * 🎯 **haugur, 2026-09-04**, with a photograph taken from the corner of
         * his row: the boards stood off the front of the houses.
         *
         * 🔴 **The model's bounding box is its ROOF.** The turf eave overhangs
         * the plank gable, so hanging the board at the box's front face put it
         * floating a third of a metre in front of the wall. ⚠️ **The number
         * looked right, and the frame it was measured in was wrong** — the same
         * shape of mistake as the sign in the sky, one step smaller.
         *
         * 🔴 **It used to be asked of the DOOR**, *"which is by definition on
         * the wall"* — **true of the door's surface and false of its bounding
         * box**, and that sentence stood here while the board hung 39 cm out in
         * front of the house. ⚠️ **A comment can be the wrong idea rather than
         * an out-of-date fact**, and this one was.
         *
         * ✅ **It is asked of the GABLE inside the board's own rectangle** —
         * `signWallZ` — and it re-measures itself if the model is replaced.
         */
        this.signWallZ = signWallZ(house);
        this.template = house;
        for (const fill of this.waiting.splice(0)) fill();
      })
      .catch((err: unknown) => {
        console.error('Storehouse: the model did not load', err);
      });
  }

  get ready(): boolean {
    return this.template !== null;
  }

  /**
   * Fill `group` with one copy per door — now, or as soon as the model is here.
   *
   * @param litAt 🔦 **How much added light a chunk-local cell has, 0–1** — the
   *   mesher's own flood, handed over rather than recomputed. **Omitted means no
   *   torches**, which is what a test and an early frame get.
   */
  place(
    group: Group,
    doors: readonly BurAt[],
    litAt: (x: number, y: number, z: number) => number = () => 0,
  ): void {
    const fill = (): void => {
      if (this.template === null) return;
      for (const door of doors) {
        const copy = this.template.clone(true);
        const { into } = burFrame(door.facing);
        const c = burCentre(door, door.facing);
        copy.position.set(c.x + 0.5, c.y, c.z + 0.5);
        copy.rotation.y = Math.atan2(into.x, into.z);
        /**
         * 🔦 **Asked at the cell just OUTSIDE the door, at head height.**
         *
         * 🔴 **Not the door's own cell** — a door is a solid block and a solid
         * block's own cell is dark, which is the same reason the mesher lights a
         * face by *"the light in the cell this face LOOKS OUT INTO"*. **Asking
         * the block itself would have returned 0 for every house forever**, and
         * it would have looked exactly like this fix not working.
         */
        const outward = burFrame(door.facing).into;
        const lit = litAt(door.x - outward.x, door.y + 1, door.z - outward.z);
        const mats = this.materialsFor(lit);
        if (mats !== null) {
          copy.traverse((o) => {
            if (!(o instanceof Mesh)) return;
            for (const kind of ['body', 'door', 'backGable'] as const) {
              /* eslint-disable-next-line security/detect-object-injection -- the
                 same three literals, iterated from a const tuple. */
              if (o.name === `bur-${kind}`) o.material = mats[kind];
            }
          });
        }
        /* 🪧 The board goes on the COPY, not the template — three buildings
           share one model and only the sign tells them apart (D110). */
        const sign = signMesh(door.kind, this.signWallZ, lit);
        if (sign !== null) copy.add(sign);
        group.add(copy);
      }
    };
    if (this.template !== null) fill();
    else this.waiting.push(fill);
  }
}

/** One group per chunk, or `null` when the chunk has no door in it. */
export function buildBurGroup(
  doors: readonly BurAt[],
  model: BurModel,
  /* 🔦 The mesher's own light flood for this chunk — see `ChunkMesh.litAt`. */
  litAt?: (x: number, y: number, z: number) => number,
): Group | null {
  if (doors.length === 0) return null;
  const group = new Group();
  model.place(group, doors, litAt);
  return group;
}
